import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { getLookupKeyForComponent } from "@/utils/jsx/extractElementsUtils";
import { JsxElement, JsxElementType } from "@/utils/jsx/jsxElementTypes";
import { ReactFramework } from "@/utils/templateProjects";
import { useMemo } from "react";
import {
  useActiveProject,
  getRelativePathFromCanvas,
  useCanvasId,
} from "@/utils/utils";

// Library Component Configuration Types
// These types represent the structure of the "libraries" section in tempo.config.json
export interface LibraryComponentConfig {
  importName?: string; // The exported name (e.g., "Button"). Undefined means default export.
  importPath: string; // Path to the file from the project root (e.g., "src/components/ui/button") - no extension
}

export interface LibraryConfig {
  id: string; // Unique identifier (UUID)
  name: string; // Display name for the library (e.g., "default", "shadcn")
  components: LibraryComponentConfig[]; // List of components in this library
}

// Libraries is an array of library configurations
export type LibrariesConfig = LibraryConfig[];

// Extended JsxElement type that includes library component information
export interface AssetComponent extends JsxElement {
  key: string;
  libraryId?: string; // Which library this component belongs to (the UUID)
}

// Helper to check if an AssetComponent is in a library (libraryId is defined)
export const isLibraryComponent = (component: AssetComponent): boolean => {
  return component.libraryId !== undefined;
};

// Helper function to check if a component matches a library config entry
const isComponentInLibraryConfig = (
  componentPath: string, // Canvas-relative path without extension (e.g., "src/components/ui/button")
  componentName: string | undefined, // The component's export name (e.g., "Button")
  librariesConfig: LibrariesConfig | undefined,
): { isLibrary: boolean; libraryId?: string } => {
  if (!librariesConfig || !Array.isArray(librariesConfig) || !componentPath) {
    return { isLibrary: false };
  }

  // Check each library configuration
  for (const libraryConfig of librariesConfig) {
    for (const libComponent of libraryConfig.components) {
      // Path must match exactly (normalized)
      const configPath = libComponent.importPath.replace(/^\/+/, ""); // Remove leading slashes
      if (componentPath !== configPath) {
        continue;
      }

      // For named exports, importName must match componentName
      // For default exports, importName is undefined and we match any component from that file
      if (
        libComponent.importName === undefined ||
        libComponent.importName === componentName
      ) {
        return { isLibrary: true, libraryId: libraryConfig.id };
      }
    }
  }

  return { isLibrary: false };
};

// Helper to extract display name from a story filename
// e.g., "/path/to/custom.stories.tsx" -> "custom"
const getStoryFileDisplayName = (filename: string): string => {
  const basename = filename.split("/").pop() || filename;
  // Remove .stories.tsx, .stories.ts, .stories.jsx, .stories.js extensions
  return basename.replace(/\.stories\.(tsx?|jsx?)$/, "");
};

// Helper to create a unique key for a story file (used for grouping)
const getStoryFileKey = (filename: string): string => {
  // Use the full path without extension as the key to ensure uniqueness
  return `storyfile:${filename.replace(/\.(tsx?|jsx?)$/, "")}`;
};

export const useAssetComponents = () => {
  const project = useActiveProject();
  const canvasId = useCanvasId();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  // Access the library configuration from tempo.config.json via conditions.config.data.libraries
  const librariesConfig = useCodebaseStore(
    (state) =>
      state.conditions?.config?.data?.libraries as LibrariesConfig | undefined,
  );

  return useMemo(() => {
    const componentKeyToComponent: { [key: string]: AssetComponent } = {};
    const componentKeyToStories: { [key: string]: JsxElement[] } = {};

    // For standalone story files (no meta.component), group by filename
    // Key: story file key -> Value: array of story exports from that file
    const standaloneStoryFileToStories: { [fileKey: string]: JsxElement[] } =
      {};
    // Track the first story element from each standalone file (used to create the virtual component)
    const standaloneStoryFileFirstElement: { [fileKey: string]: JsxElement } =
      {};

    Object.keys(treeElementLookup)?.forEach((codebaseId: string) => {
      const element: JsxElement = treeElementLookup[codebaseId];

      if (!element) {
        return;
      }

      const filenameWithoutExtension = element.filename.replace(
        /\.[^/.]+$/,
        "",
      );

      if (
        filenameWithoutExtension.includes("tempobook/dynamic") ||
        filenameWithoutExtension.includes("tempobook/storyboards")
      ) {
        return;
      }

      if (
        project?.framework === ReactFramework.NEXT_JS &&
        filenameWithoutExtension.endsWith("pages/_app")
      ) {
        return;
      }

      if (filenameWithoutExtension.endsWith("_layout")) {
        return;
      }

      if (element.type === JsxElementType.StorybookComponent) {
        // Check if this story has a meta.component reference
        if (!element.storybookMainComponentImport?.filename) {
          // No meta.component - this is a standalone story file
          // Group all stories from the same file together
          const fileKey = getStoryFileKey(element.filename);

          if (!standaloneStoryFileToStories[fileKey]) {
            standaloneStoryFileToStories[fileKey] = [];
            standaloneStoryFileFirstElement[fileKey] = element;
          }

          standaloneStoryFileToStories[fileKey].push(element);
          return;
        }

        // Has meta.component - group under the referenced component
        const key = getLookupKeyForComponent(element);

        if (!componentKeyToStories[key]) {
          componentKeyToStories[key] = [];
        }

        componentKeyToStories[key].push(element);
        return;
      }

      if (
        element.type === JsxElementType.Component ||
        element.type === JsxElementType.LibraryComponent
      ) {
        const key = getLookupKeyForComponent(element);

        // Convert absolute path to canvas-relative path for library matching
        const relativePath = getRelativePathFromCanvas(
          element.filename,
          canvasId,
        );
        const relativePathWithoutExt = relativePath.replace(/\.[^/.]+$/, "");

        // Check if this component is in the library config
        const { isLibrary, libraryId } = isComponentInLibraryConfig(
          relativePathWithoutExt,
          element.componentName,
          librariesConfig,
        );

        componentKeyToComponent[key] = {
          ...element,
          key,
          libraryId,
        };
        return;
      }
    });

    // Handle stories that reference components not in our component list
    // (e.g., external components or components that weren't parsed)
    // These should be grouped by their source file, similar to standalone stories
    Object.keys(componentKeyToStories).forEach((storyKey) => {
      const stories = componentKeyToStories[storyKey];
      if (stories.length > 0 && !componentKeyToComponent[storyKey]) {
        // The story references a component that's not in our component list
        // Group these by their source file
        stories.forEach((story) => {
          const fileKey = getStoryFileKey(story.filename);

          if (!standaloneStoryFileToStories[fileKey]) {
            standaloneStoryFileToStories[fileKey] = [];
            standaloneStoryFileFirstElement[fileKey] = story;
          }

          standaloneStoryFileToStories[fileKey].push(story);
        });
        delete componentKeyToStories[storyKey];
      }
    });

    // Create "virtual components" for each standalone story file
    // These represent the story file itself as a component in the Assets Panel
    const standaloneStoryFiles: AssetComponent[] = Object.keys(
      standaloneStoryFileToStories,
    ).map((fileKey) => {
      const firstElement = standaloneStoryFileFirstElement[fileKey];
      const displayName = getStoryFileDisplayName(firstElement.filename);

      return {
        ...firstElement,
        key: fileKey,
        // Override componentName with the file's display name
        componentName: displayName,
      };
    });

    return {
      componentKeyToStories,
      componentKeyToComponent,
      standaloneStoryFiles,
      standaloneStoryFileToStories,
      librariesConfig,
    };
  }, [treeElementLookup, librariesConfig, canvasId, project?.framework]);
};
